/**
 * Session Expiry Warning Modal
 * Displays a warning modal when the session is about to expire due to
 * inactivity. Driven by window events dispatched from App.jsx's SessionManager
 * idle callbacks; the idle logout itself is performed by SessionManager.
 */

import { useState, useEffect, useCallback } from 'react';
import sessionManager from '../../services/session/SessionManager';
import { stripStatePrefix } from '../../utils/tenant';
import styles from './SessionExpiryModal.module.css';

// Never show the warning on login / auth-callback / onboarding screens.
const AUTH_PATH_PREFIXES = ['/login', '/auth/', '/branch/auth/', '/onboard', '/account-selection', '/platform'];
const isAuthPath = () => {
  const path = stripStatePrefix(window.location.pathname || '/');
  return path === '/' || AUTH_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
};

const SessionExpiryModal = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Format time remaining for display
  const formatTime = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Handle logout (revokes server-side and signs out other tabs)
  const handleLogout = useCallback(() => {
    setIsVisible(false);
    void sessionManager.logout('user_signout');
  }, []);

  // Handle continue session: counts as activity in every tab and refreshes
  const handleContinue = useCallback(async () => {
    setIsRefreshing(true);
    setIsVisible(false);
    setTimeRemaining(0);

    try {
      const result = await sessionManager.extendSession();
      if (result?.classification === 'definitive') {
        handleLogout();
      }
    } catch (error) {
      if (!import.meta.env.PROD) {
        console.error('Failed to refresh session:', error);
      }
    } finally {
      setIsRefreshing(false);
    }
  }, [handleLogout]);

  // Listen for session expiry warning events
  useEffect(() => {
    const handleExpiryWarning = (event) => {
      if (isAuthPath()) return;
      const { timeRemaining: time } = event.detail;
      setTimeRemaining(time);
      setIsVisible(true);
    };

    // Activity elsewhere (e.g. another tab) cleared the warning, or the idle
    // deadline passed and SessionManager is logging out.
    const handleHide = () => {
      setIsVisible(false);
    };

    window.addEventListener('sessionExpiryWarning', handleExpiryWarning);
    window.addEventListener('sessionExpiryWarningCleared', handleHide);
    window.addEventListener('sessionExpired', handleHide);

    return () => {
      window.removeEventListener('sessionExpiryWarning', handleExpiryWarning);
      window.removeEventListener('sessionExpiryWarningCleared', handleHide);
      window.removeEventListener('sessionExpired', handleHide);
    };
  }, []);

  // Update countdown timer
  useEffect(() => {
    if (!isVisible || timeRemaining <= 0) return;

    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        const newTime = prev - 1000;
        if (newTime <= 0) {
          // SessionManager owns the idle logout; nudge it in case its timer
          // was throttled in the background.
          setIsVisible(false);
          sessionManager.evaluateIdle();
          return 0;
        }
        return newTime;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isVisible, timeRemaining]);

  if (!isVisible) return null;

  return (
    <>
      {/* Modal Backdrop */}
      <div className={styles.backdrop} aria-hidden="true" />
      
      {/* Modal Content */}
      <div 
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="session-expiry-title"
        aria-describedby="session-expiry-description"
      >
        <div className={styles.content}>
          {/* Icon */}
          <div className={styles.iconWrapper}>
            <svg 
              className={styles.icon}
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
              aria-hidden="true"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" 
              />
            </svg>
          </div>

          {/* Title */}
          <h2 id="session-expiry-title" className={styles.title}>
            Are you still there?
          </h2>

          {/* Description */}
          <p id="session-expiry-description" className={styles.description}>
            For your security, you will be signed out due to inactivity in{' '}
            <strong>{formatTime(timeRemaining)}</strong>.
          </p>

          {/* Progress Bar */}
          <div className={styles.progressBar}>
            <div 
              className={styles.progressFill}
              style={{ 
                width: `${Math.max(0, (timeRemaining / (2 * 60 * 1000)) * 100)}%` 
              }}
            />
          </div>

          {/* Actions */}
          <div className={styles.actions}>
            <button
              onClick={handleLogout}
              className={styles.logoutButton}
              disabled={isRefreshing}
            >
              Sign out
            </button>
            <button
              onClick={handleContinue}
              className={styles.continueButton}
              disabled={isRefreshing}
            >
              {isRefreshing ? 'Refreshing...' : 'Stay signed in'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default SessionExpiryModal;
